'use client';

import { invalidateKeys, queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type {
  EvaluateLiveMessagesBody,
  LiveEvaluation,
  LiveMonitorRun,
  LiveMonitorState,
  LiveTrafficFeed,
} from '@borradh-workspace/api-client/types';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

const BASE = 'admin-terminal/harness/live';

/** How often the live feed re-reads production while the tab is open. */
export const LIVE_POLL_MS = 10_000;

export type LiveTrafficParams = {
  conversationId?: string;
  windowHours: number;
  platform: string | null;
  organizationId: string | null;
};

export const liveTrafficQueryOptions = (params: LiveTrafficParams) =>
  queryOptions({
    queryKey: queryKeys.adminTerminal.harnessLiveFeed(params),
    queryFn: () => {
      const search = new URLSearchParams({
        since: new Date(
          Date.now() - params.windowHours * 60 * 60 * 1000
        ).toISOString(),
        limit: '200',
      });
      if (params.conversationId)
        search.set('conversationId', params.conversationId);
      if (params.platform) search.set('platform', params.platform);
      if (params.organizationId) {
        search.set('organizationId', params.organizationId);
      }
      return apiClient.get<LiveTrafficFeed>(`${BASE}/feed?${search}`);
    },
    retry: false,
  });

export const useLiveTraffic = (
  params: LiveTrafficParams,
  options: { paused: boolean }
) => {
  const query = useQuery({
    ...liveTrafficQueryOptions(params),
    refetchInterval: options.paused ? false : LIVE_POLL_MS,
    refetchIntervalInBackground: false,
  });
  return {
    feed: query.data ?? null,
    messages: query.data?.messages ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    updatedAt: query.dataUpdatedAt,
    refetch: query.refetch,
  };
};

export const useEvaluateLiveMessages = (options?: { quiet?: boolean }) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: EvaluateLiveMessagesBody) =>
      apiClient.post<LiveEvaluation[]>(`${BASE}/evaluate`, body),
    onSuccess: (evaluations) => {
      invalidateKeys(queryClient, queryKeys.adminTerminal.harnessLive());
      const flagged = evaluations.filter((e) => e.verdict === 'flag').length;
      const failed = evaluations.filter((e) => e.verdict === 'error').length;
      if (failed) {
        toast.error(
          `${failed} ${failed === 1 ? 'reply' : 'replies'} could not be checked`
        );
      } else if (!options?.quiet) {
        toast.success(
          flagged
            ? `${flagged} of ${evaluations.length} flagged`
            : `${evaluations.length} checked, nothing flagged`
        );
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not check these replies');
    },
  });
  return {
    evaluate: mutation.mutate,
    evaluateAsync: mutation.mutateAsync,
    isEvaluating: mutation.isPending,
    pendingIds: mutation.isPending
      ? (mutation.variables?.messageIds ?? [])
      : [],
  };
};

/** The production monitor's switch and recent runs; re-read every 30s. */
export const useLiveMonitor = () => {
  const query = useQuery({
    queryKey: queryKeys.adminTerminal.harnessLiveMonitor(),
    queryFn: () => apiClient.get<LiveMonitorState>(`${BASE}/monitor`),
    refetchInterval: 30_000,
    retry: false,
  });
  return {
    monitor: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
};

export const useSetLiveMonitor = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (enabled: boolean) =>
      apiClient.put<LiveMonitorState>(`${BASE}/monitor`, { enabled }),
    onSuccess: (state) => {
      queryClient.setQueryData(
        queryKeys.adminTerminal.harnessLiveMonitor(),
        state
      );
      toast.success(
        state.enabled
          ? `Monitor on: checking production every ${state.intervalMinutes} minutes`
          : 'Monitor off'
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not change the monitor');
    },
  });
  return { setMonitor: mutation.mutate, isSaving: mutation.isPending };
};

export const useRunLiveMonitor = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => apiClient.post<LiveMonitorRun>(`${BASE}/monitor/run`, {}),
    onSuccess: (run) => {
      invalidateKeys(queryClient, queryKeys.adminTerminal.harnessLive());
      if (run.error) toast.error(`Run failed: ${run.error}`);
      else
        toast.success(`${run.scored} checked, ${run.flagged.length} flagged`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not run the monitor');
    },
  });
  return { runNow: mutation.mutate, isRunning: mutation.isPending };
};
